import { Box, Button, Menu, MenuItem, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';
import TreeView from './TreeView';
import { flattenList } from '../CommonFunctions/CommonFunctions';
import { databaseImageMap } from '../DbConnection/Data';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fontSize, palette } from '../..';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { useDispatch } from 'react-redux';
import {
  RemoveArrow,
  SelectConnection,
  SetIsExtract,
  SetSelectedTables,
  UpdateConnections,
} from '../../redux/_Dataset/dataset.action';
import { AcceptRejectDialog, NotificationDialog } from '../CommonFunctions/DialogComponents';
import { colors } from '../ChartOptions/Color/ColorScheme';
import DBInfo from './DBInfo';
import WebApiInfo from './WebApiInfo';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { TAlert } from '../../types/types';
import { useLocation } from 'react-router-dom';
import SnackBar from '../SnackBar';

interface ISideBar {
  onConnectionDelete: (connectionId: string) => void;
  onTableUncheck: (tableIds: string[]) => void;
  onSidebarWidthChange: (width: number) => void;
  showSidebar: boolean; // 🔹 ADDED
  setShowSidebar: (v: boolean) => void;
  setSingleSidebar: (v: boolean) => void;
  autoOpenFlatfileTree?: boolean;
}
function Sidebar({
  onConnectionDelete,
  onTableUncheck,
  onSidebarWidthChange,
  showSidebar,
  setShowSidebar,
  setSingleSidebar,
  autoOpenFlatfileTree,
}: ISideBar) {
  const {
    connections,
    selectedConnection,
    selectedTables,
    arrows,
    canEdit,
    isEnterprise,
    isExtract,
  } = useSelector((state: RootState) => ({
    connections: state.dataset.connections,
    selectedConnection: state.dataset.selectedConnectionId,
    selectedTables: state.dataset.selectedTables,
    arrows: state.dataset.arrows,
    canEdit: state.dataset.canEdit,
    isEnterprise: state.isLogged.businessType === 'enterprise',
    isExtract: state.dataset.isExtract,
  }));
  const location = useLocation();
  const [alert, setAlert] = useState<TAlert | null>(null);
  const dispatch = useDispatch();
  const selectedConnectionObj = connections.find(
    (connection) => connection.id === selectedConnection,
  );
  const [addData, setAddData] = useState<'ff' | 'db' | 'wa' | null>(null);
  const options = [
    {
      value: 'From DB Connection',
      key: 'db',
    },
    {
      value: 'From Flatfiles',
      key: 'ff',
    },
    {
      value: 'From Web Api',
      key: 'wa',
    },
  ] as const;
  const [showDialog, setShowDialog] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackBar, setSnackBar] = useState<{ show: boolean; msg: string } | null>(null);
  function handleShowMenu(e: React.MouseEvent<HTMLButtonElement>) {
    if (!canEdit) return;
    setAnchorEl(e.currentTarget);
  }
  function handleMenuClose() {
    setAnchorEl(null);
  }
  function handleOptionClick(val: 'ff' | 'db' | 'wa') {
    if (!canEdit) return;
    setSingleSidebar(false);
    setAddData(val);
    setAnchorEl(null);
  }
  const getTableIdsToDelete = (tableId: string): string[] => {
    //for duplicate tables table id will be like this originaltableId_duplicateId
    const target = selectedTables.find((t) => t.id.split('_')[0] === tableId);

    if (!target) return [];

    const duplicates = selectedTables
      .filter((t) => t.table === target.table && t.isDuplicated)
      .map((table) => table.id);

    return duplicates.length > 0
      ? [target.id, ...duplicates] // original + duplicates
      : [target.id]; // only original
  };
  function handleProceedButtonClick(selectedId: string, list: any[], checked: boolean) {
    if (checked) {
      let tree = JSON.parse(JSON.stringify(list));

      let flatList = flattenList(tree);

      let found = flatList.find((item) => item.id === selectedId);
      function findPathById(data: any[], targetId: string) {
        const path = [];

        function dfs(nodes: any[], currentPath: any[]): any[] | null {
          for (const node of nodes) {
            const newPath = [...currentPath];

            if (node.id === targetId) {
              return newPath; // full path found
            }

            if (node.children) {
              const result = dfs(node.children, [...newPath, node.id]);
              if (result) return result;
            }
          }
          return null;
        }

        return dfs(data, []);
      }
      const path = findPathById(tree, selectedId);
      if (found) {
        const alreadyExists = connections.find((connection) => connection.id === selectedId);
        const availableConnections = connections.length;
        const idx = availableConnections % colors.length;
        const fileType = found.fileType as 'dbcon' | 'flatfile' | 'webapi';
        if (!alreadyExists) {
          const newConnection = {
            id: selectedId,
            label: found.label,
            fileType: fileType,
            workSpaceId: found.workspaceId,
            vendor: found.vendor,
            color: colors[idx],
            isNew: true,
            workspaces: path ?? undefined,
          };

          let totalDbConnections = connections.filter(
            (connection) => connection.fileType === 'dbcon',
          ).length;
          let totalFlatFiles = connections.filter(
            (connection) => connection.fileType === 'flatfile',
          ).length;
          let totalWebApi = connections.filter(
            (connection) => connection.fileType === 'webapi',
          ).length;
          if (newConnection.fileType === 'dbcon') totalDbConnections++;
          else if (newConnection.fileType === 'flatfile') totalFlatFiles++;
          else if (newConnection.fileType === 'webapi') totalWebApi++;
          if (totalFlatFiles > 10 || totalDbConnections > 5 || totalWebApi > 10) {
            setAlert({
              severity: 'warning',
              message: `Can add only upto 10 ${newConnection.fileType === 'webapi' ? 'Web API Connections' : 'FlatFiles'} per Dataset.`,
            });
            return;
          }

          dispatch(UpdateConnections([...connections, newConnection]));
          dispatch(SelectConnection(selectedId));

          if (
            isEnterprise &&
            !isExtract &&
            (totalDbConnections > 1 ||
              (totalDbConnections > 0 && totalFlatFiles > 0) ||
              totalWebApi > 0)
          ) {
            dispatch(SetIsExtract(true));
            setSnackBar({
              show: true,
              msg: 'Switched to Extract Mode',
            });
          }
          //doubt
          if (addData === 'db' || addData === 'wa') setAddData(null);
        } else {
          setAlert({
            severity: 'warning',
            message: `This ${fileType === 'dbcon' ? 'DB Connection ' : fileType === 'webapi' ? 'Web Api Connection' : 'Flatfile'} is already selected. Please use the existing connection from the list of connections.`,
          });
        }
      }
    } else {
      /**_ not include check to get actual table not duplicate */
      const table = selectedTables.find(
        (table) => table.flatFileId === selectedId && !table.id.includes('_'),
      );

      if (!table) return;
      if (!table.isNewTable) {
        setAlert({
          severity: 'warning',
          message: 'Cannot remove this flatfile .',
        });
        return;
      }
      const tableIdsToDelete = getTableIdsToDelete(table.id);
      if (tableIdsToDelete.length === 0) return;
      const arrowIdsToBeDeleted = [
        ...new Set(
          arrows
            .filter(
              (arrow) =>
                tableIdsToDelete.includes(arrow.startId) || tableIdsToDelete.includes(arrow.endId),
            )
            .map((arrow) => arrow.arrowId),
        ),
      ];
      dispatch(RemoveArrow(arrowIdsToBeDeleted));
      onTableUncheck(tableIdsToDelete);
      dispatch(
        SetSelectedTables(
          selectedTables.filter((table) => !tableIdsToDelete.includes(table.id)),
          //no need to modify order as its handled byu react flow
        ),
      );

      const newConnections = connections.filter((connection) => connection.id !== selectedId);
      /**
       *
       * no need to call onConnectionDelete as it removes dataset filters but that will be removed by onTableUnCheck()
       */
      dispatch(UpdateConnections(newConnections));
      dispatch(SelectConnection(''));
    }
  }

  function handleConnectionSelection(connectionId: string) {
    if (connectionId === selectedConnection) return;
    dispatch(SelectConnection(connectionId));
  }
  function handleConnectionDelete() {
    const toBeDeletedTables: string[] = [];
    const newSelectedTables = selectedTables.filter((table) => {
      if (
        (table.isFlatFile && table.flatFileId === selectedConnection) ||
        (!table.isFlatFile && table.connectionId === selectedConnection)
      )
        toBeDeletedTables.push(table.table);

      return table.isFlatFile
        ? table.flatFileId !== selectedConnection
        : table.connectionId !== selectedConnection;
    });
    /** remove selected tables */
    dispatch(SetSelectedTables(newSelectedTables));
    /**remove  */
    const arrowIdsToBeDeleted: string[] = [];
    arrows.forEach((arrow) => {
      if (
        toBeDeletedTables.includes(arrow.startTableName) ||
        toBeDeletedTables.includes(arrow.endTableName)
      ) {
        arrowIdsToBeDeleted.push(arrow.arrowId);
      }
    });
    dispatch(RemoveArrow(arrowIdsToBeDeleted));
    onConnectionDelete(selectedConnection!);
    const newConnections = connections.filter((connection) => connection.id !== selectedConnection);
    dispatch(UpdateConnections(newConnections));
    dispatch(SelectConnection(''));
    setShowDialog(null);

    let totalDbConnections = newConnections.filter(
      (connection) => connection.fileType === 'dbcon',
    ).length;
    let totalFlatFiles = newConnections.filter(
      (connection) => connection.fileType === 'flatfile',
    ).length;
    let totalWebApi = newConnections.filter(
      (connection) => connection.fileType === 'webapi',
    ).length;

    /**set extract to true if there are multiple db connections, combination of flatfile and db connections, or any web api connection */
    if (
      isEnterprise &&
      !isExtract &&
      (totalDbConnections > 1 || (totalDbConnections > 0 && totalFlatFiles > 0) || totalWebApi > 0)
    ) {
      dispatch(SetIsExtract(true));
      setSnackBar({
        show: true,
        msg: 'Switched to Extract Mode',
      });
    } else dispatch(SetIsExtract(false));
  }

  function disableOption(option: 'ff' | 'db' | 'wa') {
    const flatfileCount = connections.filter(
      (connection) => connection.fileType === 'flatfile',
    ).length;
    const dbCount = connections.filter((connection) => connection.fileType === 'dbcon').length;
    const webapiCount = connections.filter((connection) => connection.fileType === 'webapi').length;
    const firstConnectionType = connections[0]?.fileType;

    /* no connections yet — apply limit normally */
    if (!firstConnectionType) {
      if (option === 'ff') {
        return flatfileCount > 9;
      }
      if (option === 'wa') {
        return webapiCount > 9;
      }
      return dbCount > (isEnterprise ? 4 : 0);
    }

    /**
      if first connection type exists that means  there is already a connection selected  flatfile or db connection
    */
    if (!isEnterprise) {
      /*If not enterprise subscription, then we will support only one DB connection */
      if (firstConnectionType === 'dbcon') return true;
      /* max supported flatfiles/webapis are 10 in enterprise or non_enterprise subscription */
      if (option === 'db') {
        return true;
      } else if (option === 'wa') {
        return webapiCount > 9;
      }
      return flatfileCount > 9;
    } else {
      if (option === 'ff') return flatfileCount > 9;
      if (option === 'wa') return webapiCount > 9;
      return dbCount > 4;
    }
  }

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  useEffect(() => {
    const widthRem = showSidebar ? 15 : 4;
    onSidebarWidthChange(widthRem);
  }, [showSidebar, isSmallScreen, onSidebarWidthChange]);

  useEffect(() => {
    if (autoOpenFlatfileTree) {
      setShowSidebar(true);
      setSingleSidebar(false);
      setAddData('ff');
    }
  }, [autoOpenFlatfileTree]);
  return (
    <Box
      id="sidebar"
      sx={{
        padding: 1,
        height: '100%',
        maxWidth: showSidebar
          ? isSmallScreen
            ? '60vw'
            : selectedConnectionObj?.fileType === 'webapi'
              ? '40rem'
              : '30rem'
          : '4rem',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        overflowX: 'hidden',
        position: 'relative',
        overflowY: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          borderRight: selectedConnection ? '1px solid #e0e0e0' : 'none',
          pr: 1,
          paddingLeft: showSidebar ? 0 : '5rem',
          width: addData ? (isSmallScreen ? '80vw' : '30rem') : isSmallScreen ? '15vw' : '15rem',
        }}
      >
        {!addData && (
          <div
            id="add-data-button-wrapper"
            style={{
              textAlign: 'left',
              marginTop: '12.5px',
            }}
          >
            <Button
              sx={{
                fontSize: {
                  xs: '10px',
                  sm: '11px',
                  md: '12px',
                },
                color: 'white',
                backgroundColor: 'primary.main',
                px: 1,
                py: 0.5,
                width: '100%',
                minWidth: 0,
                textTransform: 'none',
              }}
              onClick={handleShowMenu}
              disabled={!canEdit}
            >
              Add Data
            </Button>

            <Menu
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={handleMenuClose}
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
              }}
            >
              {options.map((option) => {
                const disabled = disableOption(option.key);
                return (
                  <MenuItem
                    id={option.key}
                    onClick={() => !disabled && handleOptionClick(option.key)}
                    disabled={disabled}
                  >
                    {option.value}
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
        )}
        {addData ? (
          <TreeView
            type={addData}
            setFunction={setAddData}
            proceedFunction={handleProceedButtonClick}
            currentWorkspace={location.state?.parentId ?? ''}
            autoOpenCurrentWorkspace={location.state?.openCurrentWorkspace}
          />
        ) : (
          <Box id="connection-list" sx={{ mt: 2, height: '100%' }}>
            {connections.length === 0 ? (
              <span>Add a connection or flatfile</span>
            ) : (
              connections.map((connection) => (
                <Box
                  onClick={() => {
                    handleConnectionSelection(connection.id);
                  }}
                  key={connection.id}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1,
                    pl: 0.5,
                    my: '0.5rem',
                    transition: 'padding 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 0 0 1px #e0e0e0`,
                    },
                    outline: 'none',
                    borderRadius: '5px',
                    boxShadow:
                      connection.id === selectedConnection
                        ? `0 0 0 1px ${connection.color}`
                        : '0 0 0 1px #e7e7e7ff',
                    borderLeft: `3px solid ${connection.color}`,
                  }}
                >
                  {connection.fileType === 'webapi' ? (
                    <LanguageIcon
                      sx={{ fontSize: 14, color: 'text.secondary', ml: '0.5rem', flexShrink: 0 }}
                    />
                  ) : (
                    <img
                      src={
                        connection.fileType === 'flatfile'
                          ? '/flat_file_icon.svg'
                          : databaseImageMap[connection.vendor ?? 'default']
                      }
                      alt={connection.label}
                      className="vendorIconStyle"
                      style={{
                        height: '14px',
                        width: 14,
                        marginLeft: '0.5rem',
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      maxWidth: '200px', // or adjust as needed
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {connection.label}
                  </Typography>
                  <div
                    className=""
                    style={{
                      marginLeft: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CloseRoundedIcon
                      onClick={() => {
                        if (!connection.isNew || !canEdit) {
                          setAlert({
                            severity: 'warning',
                            message: 'Cannot delete the connection.',
                          });
                          return;
                        }
                        setShowDialog(connection.label);
                      }}
                      sx={{
                        fontSize: fontSize.medium,
                        cursor: 'pointer',
                        color: palette.primary.contrastText,
                        marginRight: '0.5rem',
                        '&:hover': {
                          color: 'red',
                          transform: 'scale(1.1)',
                        },
                      }}
                    />
                  </div>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>
      {(!addData || addData === 'ff') && selectedConnectionObj?.fileType !== 'webapi' && (
        <DBInfo onTableUncheck={onTableUncheck} showFullWidth={addData !== 'ff'} />
      )}
      {!addData && selectedConnectionObj?.fileType === 'webapi' && (
        <WebApiInfo onTableUncheck={onTableUncheck} />
      )}
      {showDialog && (
        <AcceptRejectDialog
          open={!!showDialog}
          acceptFunction={handleConnectionDelete}
          rejectFunction={() => setShowDialog(null)}
          closeFunction={() => setShowDialog(null)}
          heading={`Are you sure?`}
          messages={[
            {
              text: `Do you want to remove ${showDialog}`,
              highlights: [
                {
                  substring: `${showDialog}`,
                  style: {
                    fontWeight: 'bold',
                  },
                },
              ],
            },
            {
              text: `Any table coming from the connection will be removed`,
            },
          ]}
          acceptText="Ok"
          rejectText="Cancel"
          rejectButtonStyle={{
            border: '1px solid gray',
            color: 'gray',
          }}
          acceptButtonStyle={{
            border: '1px solid gray',
            color: 'gray',
          }}
          acceptTransition={{
            color: 'primary.contastText',
            transitionTime: '0.3s',
            backgroundColor: 'secondary.contrastText',
            hoverColor: 'secondary.contrastText',
            hoverBackgroundColor: 'primary.contrastText',
          }}
          varient="animated"
        />
      )}
      {!addData && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '0.1rem',
            left: '0.5rem',
            height: '50px',
            width: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: '#ececec',
            },
          }}
          onClick={() => {
            setShowSidebar(!showSidebar);
          }}
        >
          {!showSidebar ? (
            <ArrowForwardRoundedIcon
              fontSize="small"
              style={{
                color: palette.primary.contrastText,
                width: 20,
                height: 20,
              }}
            />
          ) : (
            <ArrowBackRoundedIcon
              fontSize="small"
              style={{
                color: palette.primary.contrastText,
                marginLeft: '1px',
                width: 20,
                height: 20,
              }}
            />
          )}
        </Box>
      )}
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert.severity}
          testMessage={alert.message}
          openAlert={!!alert}
        />
      )}
      {isEnterprise && snackBar && (
        <SnackBar
          show={snackBar.show}
          message={snackBar.msg}
          onClose={() => setSnackBar(null)}
          sx={{
            '.MuiSnackbarContent-root': {
              backgroundColor: 'rgb(231, 219, 179)',
              height: '3rem',
              color: 'primary.contrastText',
            },
          }}
          dir={{ vertical: 'bottom', horizontal: 'center' }}
        />
      )}
    </Box>
  );
}

export default Sidebar;
